/**
 * Google Analytics 4 (GA4) Integration
 * 
 * This file handles all Google Analytics tracking for the website
 */

import ReactGA from 'react-ga4';

// Initialize Google Analytics
export const initGA = () => {
  const measurementId = import.meta.env.VITE_GA_MEASUREMENT_ID;
  
  if (!measurementId) {
    console.warn('⚠️ Google Analytics Measurement ID not found. Analytics will not be tracked.');
    console.warn('Add VITE_GA_MEASUREMENT_ID to your .env.local file');
    return;
  }

  try {
    ReactGA.initialize(measurementId, {
      gaOptions: {
        // Anonymize IP addresses for privacy
        anonymizeIp: true,
        // Cookie settings
        cookieFlags: 'SameSite=None;Secure',
      },
      gtagOptions: {
        // Send page views automatically (we'll control this manually)
        send_page_view: false,
      },
    });

    console.log('✅ Google Analytics initialized with ID:', measurementId);
  } catch (error) {
    console.error('❌ Error initializing Google Analytics:', error);
  }
};

/**
 * Track page view
 */
export const trackPageView = (path: string, title?: string) => {
  try {
    ReactGA.send({ 
      hitType: 'pageview', 
      page: path,
      title: title || document.title
    });
    
    console.log('📊 GA4 Page view tracked:', path);
  } catch (error) {
    console.error('❌ Error tracking page view:', error);
  }
};

/**
 * Track custom event
 */
export const trackEvent = (
  category: string,
  action: string,
  label?: string,
  value?: number
) => {
  try {
    ReactGA.event({
      category,
      action,
      label,
      value,
    });
    
    console.log('📊 GA4 Event tracked:', { category, action, label, value });
  } catch (error) {
    console.error('❌ Error tracking event:', error);
  }
};

/**
 * Track button click
 */
export const trackButtonClick = (buttonName: string, location?: string) => {
  trackEvent('Button', 'Click', `${buttonName}${location ? ` - ${location}` : ''}`);
};

/**
 * Track signature action
 */
export const trackSignatureAction = (action: string, template?: string) => {
  trackEvent('Signature', action, template);
};

/**
 * Track conversion (sign up, purchase, etc.)
 */
export const trackConversion = (type: string, value?: number) => {
  try {
    ReactGA.event(type, {
      currency: 'USD',
      value: value || 0,
    });
    
    console.log('📊 GA4 Conversion tracked:', type, value);
  } catch (error) {
    console.error('❌ Error tracking conversion:', error);
  }
};

/**
 * Track user sign up
 */
export const trackSignUp = (method: string = 'email') => {
  trackConversion('sign_up', 0);
  trackEvent('User', 'Sign Up', method);
};

/**
 * Track user login
 */
export const trackLogin = (method: string = 'email') => {
  trackConversion('login', 0);
  trackEvent('User', 'Login', method);
};

/**
 * Track template selection
 */
export const trackTemplateSelect = (templateName: string) => {
  trackEvent('Template', 'Select', templateName);
};

/**
 * Track signature export
 */
export const trackSignatureExport = (format: string) => {
  trackEvent('Signature', 'Export', format);
};

/**
 * Track pricing plan view
 */
export const trackPricingView = () => {
  trackEvent('Pricing', 'View', 'Pricing Page');
};

/**
 * Track checkout initiation
 */
export const trackCheckoutStart = (plan: string, amount: number) => {
  try {
    ReactGA.event('begin_checkout', {
      currency: 'USD',
      value: amount,
      items: [{
        item_id: plan,
        item_name: `${plan} Plan`,
        price: amount,
        quantity: 1,
      }],
    });
    
    console.log('📊 GA4 Checkout started:', plan, amount);
  } catch (error) {
    console.error('❌ Error tracking checkout:', error);
  }
};

/**
 * Track purchase completion
 */
export const trackPurchase = (
  transactionId: string,
  plan: string,
  amount: number
) => {
  try {
    ReactGA.event('purchase', {
      transaction_id: transactionId,
      currency: 'USD',
      value: amount,
      items: [{
        item_id: plan,
        item_name: `${plan} Plan`,
        price: amount,
        quantity: 1,
      }],
    });
    
    console.log('📊 GA4 Purchase tracked:', transactionId, plan, amount);
  } catch (error) {
    console.error('❌ Error tracking purchase:', error);
  }
};

/**
 * Track search
 */
export const trackSearch = (searchTerm: string) => {
  try {
    ReactGA.event('search', {
      search_term: searchTerm,
    });
    
    console.log('📊 GA4 Search tracked:', searchTerm);
  } catch (error) {
    console.error('❌ Error tracking search:', error);
  }
};

/**
 * Track outbound link click
 */
export const trackOutboundLink = (url: string, label?: string) => {
  trackEvent('Outbound Link', 'Click', label || url);
};

/**
 * Set user properties
 */
export const setUserProperties = (properties: Record<string, any>) => {
  try {
    ReactGA.set(properties);
    console.log('📊 GA4 User properties set:', properties);
  } catch (error) {
    console.error('❌ Error setting user properties:', error);
  }
};

/**
 * Set user ID (for authenticated users)
 */
export const setUserId = (userId: string) => {
  try {
    ReactGA.set({ userId });
    console.log('📊 GA4 User ID set:', userId);
  } catch (error) {
    console.error('❌ Error setting user ID:', error);
  }
};

/**
 * Check if GA is initialized
 */
export const isGAInitialized = (): boolean => {
  return !!import.meta.env.VITE_GA_MEASUREMENT_ID;
};

// ==========================================
// Intent & Pricing Funnel Tracking
// ==========================================

/**
 * Track intent selection during onboarding
 */
export const trackIntentSelect = (intent: string, userType: 'personal' | 'company') => {
  try {
    ReactGA.event('intent_select', {
      intent,
      user_type: userType,
    });
    
    // Also set as user property for future events
    setUserProperties({
      user_intent: intent,
      user_type: userType,
    });
    
    console.log('📊 GA4 Intent selected:', { intent, userType });
  } catch (error) {
    console.error('❌ Error tracking intent selection:', error);
  }
};

/**
 * Track user type selection (personal/company)
 */
export const trackUserTypeSelect = (userType: 'personal' | 'company') => {
  try {
    ReactGA.event('user_type_select', {
      user_type: userType,
    });
    
    console.log('📊 GA4 User type selected:', userType);
  } catch (error) {
    console.error('❌ Error tracking user type selection:', error);
  }
};

/**
 * Track pricing page view with intent context
 */
export const trackPricingViewWithIntent = (
  intent: string | null, 
  userType: string | null, 
  recommendedTier: string
) => {
  try {
    ReactGA.event('pricing_page_view', {
      user_intent: intent || 'none',
      user_type: userType || 'unknown',
      recommended_tier: recommendedTier,
    });
    
    console.log('📊 GA4 Pricing page view:', { intent, userType, recommendedTier });
  } catch (error) {
    console.error('❌ Error tracking pricing page view:', error);
  }
};

/**
 * Track checkout start with intent context
 */
export const trackCheckoutStartWithIntent = (
  plan: string, 
  amount: number,
  intent: string | null,
  userType: string | null
) => {
  try {
    ReactGA.event('begin_checkout', {
      currency: 'USD',
      value: amount,
      user_intent: intent || 'none',
      user_type: userType || 'unknown',
      items: [{
        item_id: plan,
        item_name: `${plan} Plan`,
        price: amount,
        quantity: 1,
      }],
    });
    
    console.log('📊 GA4 Checkout started with intent:', { plan, amount, intent, userType });
  } catch (error) {
    console.error('❌ Error tracking checkout with intent:', error);
  }
};

/**
 * Track engagement form submission
 */
export const trackEngagementFormSubmit = (
  companySize: string,
  expectedVolume: string,
  useCase: string
) => {
  try {
    ReactGA.event('engagement_form_submit', {
      company_size: companySize,
      expected_volume: expectedVolume,
      use_case: useCase,
    });
    
    // Also track as a conversion
    trackConversion('engagement_lead', 0);
    
    console.log('📊 GA4 Engagement form submitted:', { companySize, expectedVolume, useCase });
  } catch (error) {
    console.error('❌ Error tracking engagement form:', error);
  }
};

/**
 * Track engagement user entering generator
 * Measures conversion from /engagement landing → generator
 */
export const trackEngagementGeneratorEntry = (
  source: 'landing_cta' | 'form_success' | 'direct',
  passTemplate?: string
) => {
  try {
    ReactGA.event('engagement_generator_entry', {
      source,
      pass_template: passTemplate || 'coupon',
      timestamp: new Date().toISOString(),
    });
    
    console.log('📊 GA4 Engagement user entered generator:', { source, passTemplate });
  } catch (error) {
    console.error('❌ Error tracking engagement generator entry:', error);
  }
};

/**
 * Track engagement user creating a pass
 * Measures engagement → pass creation conversion
 */
export const trackEngagementPassCreated = (
  passTemplate: string,
  passType: 'temp' | 'saved'
) => {
  try {
    ReactGA.event('engagement_pass_created', {
      pass_template: passTemplate,
      pass_type: passType,
      timestamp: new Date().toISOString(),
    });
    
    // Track as a conversion for engagement funnel
    trackConversion('engagement_pass_created', 0);
    
    console.log('📊 GA4 Engagement pass created:', { passTemplate, passType });
  } catch (error) {
    console.error('❌ Error tracking engagement pass created:', error);
  }
};

/**
 * Track engagement upsell banner click
 */
export const trackEngagementUpsellClick = (
  location: 'wallet_tab' | 'generator_header' | 'success_modal'
) => {
  try {
    ReactGA.event('engagement_upsell_click', {
      location,
      timestamp: new Date().toISOString(),
    });

    console.log('📊 GA4 Engagement upsell clicked:', { location });
  } catch (error) {
    console.error('❌ Error tracking engagement upsell click:', error);
  }
};

// ==========================================
// Intent Lifecycle Tracking
// ==========================================

/**
 * Track when user intent is set (from URL param, onboarding, or change)
 * Fires on: URL param detected, onboarding quiz completion, intent change button
 */
export const trackIntentSet = (
  intent: string,
  source: 'url_param' | 'onboarding' | 'change' | 'default',
  previousIntent?: string | null
) => {
  try {
    ReactGA.event('intent_set', {
      intent,
      source,
      previous_intent: previousIntent || 'none',
      timestamp: new Date().toISOString(),
    });

    // Set as user property for segmentation
    setUserProperties({ user_intent: intent });

    console.log('📊 GA4 Intent set:', { intent, source, previousIntent });
  } catch (error) {
    console.error('❌ Error tracking intent set:', error);
  }
};

/**
 * Track when user completes a key action matching their intent
 * Fires on: signature saved (signature_only/identity), wallet pass created (wallet_only/identity_wallet),
 * card shared (identity), team template deployed (team_identity/team_full)
 */
export const trackIntentConverted = (
  intent: string,
  conversionType: 'signature_saved' | 'wallet_pass_created' | 'card_shared' | 'team_deployed' | 'pass_distributed',
  metadata?: Record<string, any>
) => {
  try {
    ReactGA.event('intent_converted', {
      intent,
      conversion_type: conversionType,
      timestamp: new Date().toISOString(),
      ...metadata,
    });

    // Also fire as a custom conversion for funnel analysis
    trackConversion('intent_conversion', 0);

    console.log('📊 GA4 Intent converted:', { intent, conversionType, metadata });
  } catch (error) {
    console.error('❌ Error tracking intent conversion:', error);
  }
};

/**
 * Track when user upgrades tier (from intent context)
 * Fires on: successful Stripe checkout completion
 */
export const trackIntentUpgraded = (
  intent: string | null,
  fromTier: string,
  toTier: string,
  amount: number
) => {
  try {
    ReactGA.event('intent_upgraded', {
      intent: intent || 'none',
      from_tier: fromTier,
      to_tier: toTier,
      upgrade_value: amount,
      currency: 'USD',
      timestamp: new Date().toISOString(),
    });

    console.log('📊 GA4 Intent upgraded:', { intent, fromTier, toTier, amount });
  } catch (error) {
    console.error('❌ Error tracking intent upgrade:', error);
  }
};
